/**
 * Client for the Bullseye tab (epic 2026-10-05-bullseye):
 * `GET /api/web/v2/searches/{id}/bullseye` — the search's applied AI Review
 * lists and every reviewed person's scores — and `GET`/`PUT
 * /api/web/v2/searches/{id}/bullseye/settings` — the ring mode and boundaries
 * the whole team shares for the search (last writer wins).
 *
 * Types come from `@nu-assist/contracts` (generated from the API's OpenAPI).
 * Auth: makeAuthedFetch (401 → refresh → retry once), as every web client.
 */
import type {
  BullseyeSettings,
  BullseyeSettingsValidationError,
  SearchBullseye,
  UpdateBullseyeSettingsRequest,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

import type { FetchLike } from "./imports.client";

export interface BullseyeClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

/** One FastAPI field error inside a 422's `detail` list. */
export interface BullseyeFieldError {
  loc?: (string | number)[];
  msg?: string;
}

/**
 * A failed request. `detail` is the 422 body's `detail` as sent: a string for
 * a broken boundary rule (shown verbatim), or FastAPI's list of field errors.
 */
export class BullseyeApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    public readonly detail: BullseyeSettingsValidationError["detail"] | null = null
  ) {
    super(typeof detail === "string" ? detail : `Bullseye request failed (${status})`);
    this.name = "BullseyeApiError";
    Object.setPrototypeOf(this, BullseyeApiError.prototype);
  }

  /** The field errors of a 422 that did not parse, or `[]`. */
  get fieldErrors(): BullseyeFieldError[] {
    return Array.isArray(this.detail) ? (this.detail as BullseyeFieldError[]) : [];
  }
}

async function readDetail(
  res: Response
): Promise<BullseyeSettingsValidationError["detail"] | null> {
  try {
    const body = (await res.json()) as Partial<BullseyeSettingsValidationError> | null;
    const detail = body?.detail;
    return typeof detail === "string" || Array.isArray(detail) ? detail : null;
  } catch {
    return null;
  }
}

export function createBullseyeClient(cfg: BullseyeClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${cfg.apiBase}${path}`;
    const res = await authedFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new BullseyeApiError(res.status, url, await readDetail(res));
    return (await res.json()) as T;
  }

  const ofSearch = (searchId: string) =>
    `/api/web/v2/searches/${encodeURIComponent(searchId)}/bullseye`;

  return {
    /** The search's applied AI Review lists with every reviewed person's scores. */
    get(searchId: string): Promise<SearchBullseye> {
      return json("GET", ofSearch(searchId));
    },
    /** The search's saved ring mode and boundaries, or the defaults (`is_default`). */
    getSettings(searchId: string): Promise<BullseyeSettings> {
      return json("GET", `${ofSearch(searchId)}/settings`);
    },
    /** Replace the whole settings row; the response is what was saved. */
    saveSettings(searchId: string, body: UpdateBullseyeSettingsRequest): Promise<BullseyeSettings> {
      return json("PUT", `${ofSearch(searchId)}/settings`, body);
    },
  };
}

export type BullseyeClient = ReturnType<typeof createBullseyeClient>;
